// ============================================================================
// Facebook Messenger Webhook Adapter
//
// Implements the `WebhookAdapter` contract so the ingress pipeline can:
//   1. Verify the `x-hub-signature-256` HMAC against the raw request body
//      using the app secret (from `env.FB_APP_SECRET`).
//   2. Parse the Messenger webhook envelope (`entry[].messaging[]`) into
//      canonical capability events (`message.received`, `message.delivered`,
//      `message.read`) that downstream modules (chat, AI) consume.
//
// Canonical docs:
//   - https://developers.facebook.com/docs/messenger-platform/webhooks
//   - https://developers.facebook.com/docs/messenger-platform/webhooks/reference/messages
//
// Delivery / read event shapes use the authoritative mid (message id) for
// dedupe when available, and synthesise a stable id from watermark when not
// (e.g. read receipts cover every message ≤ watermark, no single mid).
// ============================================================================

import type {
  MessageAttachment,
  MessageDeliveredEvent,
  MessageReadEvent,
  MessageReceivedEvent,
} from '../../capabilities/messaging'
import {
  hmac,
  parsePrefixedSignature,
  hexToBytes,
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'

// ============================================================================
// Raw Facebook payload shape — matches the subset of fields we care about.
// Everything is `unknown`-safe: FB occasionally adds fields (or sends test
// pings with no `messaging` key) and the parser must tolerate both.
// ============================================================================

interface FacebookWebhookPayload {
  object?: string
  entry?: ReadonlyArray<FacebookEntry>
}

interface FacebookEntry {
  /** Page id that owns the messaging events. */
  id?: string
  /** Millisecond timestamp the webhook was emitted. */
  time?: number
  /** Page messaging events (Messenger). */
  messaging?: ReadonlyArray<FacebookMessagingEvent>
}

interface FacebookMessagingEvent {
  sender?: { id?: string }
  recipient?: { id?: string }
  /** Milliseconds. */
  timestamp?: number
  message?: FacebookMessage
  delivery?: FacebookDelivery
  read?: FacebookRead
  postback?: FacebookPostback
}

interface FacebookMessage {
  mid?: string
  text?: string
  attachments?: ReadonlyArray<FacebookAttachment>
  /**
   * FB sends `is_echo` for outbound Page messages. We surface those as Page
   * echoes so replies sent from Meta Business Suite appear in Kubdee without
   * being mistaken for a Kubdee staff user or AI bot. The chat hook de-dupes
   * echoes for messages Kubdee already persisted through the Send API.
   */
  is_echo?: boolean
  quick_reply?: { payload?: string }
}

interface FacebookAttachment {
  type?: string
  payload?: { url?: string; title?: string; [k: string]: unknown }
  title?: string
}

interface FacebookDelivery {
  mids?: ReadonlyArray<string>
  watermark?: number
}

interface FacebookRead {
  watermark?: number
}

interface FacebookPostback {
  title?: string
  payload?: string
  mid?: string
}

// ============================================================================
// verifyWebhook
// ============================================================================

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  // Missing secret → fail closed. We never want to accept webhooks when the
  // app secret is misconfigured; returning `false` is safer than throwing
  // because the ingress pipeline treats exceptions as verification failure
  // anyway, but this keeps response timings uniform across error modes.
  const appSecret = pickSecret(ctx.env)
  if (!appSecret) return false

  const header = ctx.headers.get('x-hub-signature-256')
  if (!header) return false

  const candidateHex = parsePrefixedSignature(header, 'sha256')
  if (!candidateHex) return false

  // Convert the hex candidate to raw bytes and compute the expected HMAC.
  // `timingSafeEqualBytes` then runs a constant-time XOR. We deliberately
  // never compare with string `===` — the header arrives unsanitized and a
  // naive compare leaks timing info (nth byte).
  let candidateBytes: Uint8Array
  try {
    candidateBytes = hexToBytes(candidateHex.trim())
  } catch {
    return false
  }

  let expectedBytes: Uint8Array
  try {
    expectedBytes = await hmac(appSecret, ctx.rawBody, 'SHA-256')
  } catch {
    return false
  }

  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

/** Pick FB_APP_SECRET from the env bag, tolerating absence / non-string values. */
function pickSecret(env: Record<string, unknown>): string | null {
  const raw = env?.FB_APP_SECRET
  if (typeof raw !== 'string' || raw.length === 0) return null
  return raw
}

// ============================================================================
// parseWebhook
// ============================================================================

async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  const text = new TextDecoder().decode(new Uint8Array(ctx.rawBody))

  // JSON.parse on a malformed body throws — the ingress pipeline treats any
  // parser exception as `invalid_payload` (400), which is exactly what we
  // want. DO NOT swallow this.
  const payload = JSON.parse(text) as FacebookWebhookPayload

  const entries = Array.isArray(payload?.entry) ? payload.entry : []
  const events: ParsedWebhookEvent[] = []

  for (const entry of entries) {
    const pageId = typeof entry?.id === 'string' ? entry.id : ''
    const messaging = Array.isArray(entry?.messaging) ? entry.messaging : []

    for (const event of messaging) {
      const senderId = typeof event?.sender?.id === 'string' ? event.sender.id : ''
      const recipientId = typeof event?.recipient?.id === 'string' ? event.recipient.id : ''
      const timestamp = typeof event?.timestamp === 'number' ? event.timestamp : 0
      const receivedAt = isoFromTimestamp(timestamp)

      // 1. Inbound message (text / attachments / quick_reply tap)
      if (event.message) {
        const mid = typeof event.message.mid === 'string' ? event.message.mid : ''
        if (mid.length === 0) continue

        const isEcho = event.message.is_echo === true
        const conversationUserId = isEcho ? recipientId : senderId
        if (conversationUserId.length === 0) continue

        const attachments = toAttachments(event.message.attachments)
        const receivedPayload: MessageReceivedEvent = {
          capability: 'messaging',
          event: 'message.received',
          // `instanceId` + `companyId` are stamped by the consuming service —
          // the adapter has no visibility into platform ids.
          instanceId: '',
          companyId: '',
          receivedAt,
          providerMessageId: mid,
          senderId: conversationUserId,
          ...(isEcho ? { direction: 'outbound', senderType: 'page' } : {}),
          ...(event.message.text !== undefined ? { text: event.message.text } : {}),
          ...(attachments.length > 0 ? { attachments } : {}),
        }

        events.push({
          eventId: mid,
          eventType: 'message.received',
          capability: 'messaging',
          payload: receivedPayload,
        })
        continue
      }

      // 2. Postback (button tap / persistent menu)
      if (event.postback) {
        const postbackPayload = typeof event.postback.payload === 'string'
          ? event.postback.payload
          : ''
        // FB guarantees neither mid nor payload in every postback. Build a
        // RETRY-STABLE synthetic id so the ingress dedupe collapses redelivery.
        // FB retries can re-stamp `timestamp` (the delivery attempt time) so
        // we deliberately exclude it; the payload + sender + base64-safe
        // separator give us a stable shape even when `postbackPayload`
        // contains `:` (avoids the `postback:s:BOOK:123` ambiguity).
        const mid = typeof event.postback.mid === 'string' ? event.postback.mid : ''
        const eventId = mid.length > 0
          ? mid
          : `postback:${encodeURIComponent(senderId)}:${encodeURIComponent(postbackPayload)}`

        const text = postbackPayload.length > 0
          ? postbackPayload
          : (typeof event.postback.title === 'string' ? event.postback.title : '')

        const postbackEvent: MessageReceivedEvent = {
          capability: 'messaging',
          event: 'message.received',
          instanceId: '',
          companyId: '',
          receivedAt,
          providerMessageId: eventId,
          senderId,
          text,
        }

        events.push({
          eventId,
          eventType: 'message.received',
          capability: 'messaging',
          payload: postbackEvent,
        })
        continue
      }

      // 3. Delivery receipt — FB batches mids[] for messages it has delivered
      if (event.delivery && Array.isArray(event.delivery.mids)) {
        const deliveredAt = isoFromTimestamp(
          typeof event.delivery.watermark === 'number' ? event.delivery.watermark : timestamp,
        )
        for (const mid of event.delivery.mids) {
          if (typeof mid !== 'string' || mid.length === 0) continue
          const deliveredPayload: MessageDeliveredEvent = {
            capability: 'messaging',
            event: 'message.delivered',
            instanceId: '',
            companyId: '',
            providerMessageId: mid,
            deliveredAt,
          }
          // `mid` alone is unique + stable; FB retries CAN re-stamp the
          // delivery timestamp, so including it would break dedupe. The
          // ingress pipeline uses the FIRST event id in this batch so a
          // collision between two delivery rows only matters if the same
          // mid appears twice — which FB does not do.
          events.push({
            eventId: `delivery:${mid}`,
            eventType: 'message.delivered',
            capability: 'messaging',
            payload: deliveredPayload,
          })
        }
        continue
      }

      // 4. Read receipt — covers every message ≤ watermark. No per-mid fanout.
      if (event.read && typeof event.read.watermark === 'number') {
        const readAt = isoFromTimestamp(event.read.watermark)
        const readPayload: MessageReadEvent = {
          capability: 'messaging',
          event: 'message.read',
          instanceId: '',
          companyId: '',
          providerMessageId: String(event.read.watermark),
          readAt,
        }
        events.push({
          eventId: `read:${pageId}:${event.read.watermark}`,
          eventType: 'message.read',
          capability: 'messaging',
          payload: readPayload,
        })
        continue
      }

      // Any other shape (account_linking, opt-in, echo, …) is ignored on
      // purpose — we only surface the messaging subset the chat module
      // consumes today.
    }
  }

  return events
}

// ============================================================================
// Attachment mapping — FB shape → capability shape
// ============================================================================

function toAttachments(input: ReadonlyArray<FacebookAttachment> | undefined): MessageAttachment[] {
  if (!Array.isArray(input)) return []

  const out: MessageAttachment[] = []
  for (const raw of input) {
    const type = typeof raw?.type === 'string' ? raw.type : ''
    const url = typeof raw?.payload?.url === 'string' ? raw.payload.url : ''
    if (!url) continue

    switch (type) {
      case 'image': {
        const altTitle =
          typeof raw.title === 'string'
            ? raw.title
            : typeof raw.payload?.title === 'string'
              ? raw.payload.title
              : undefined
        out.push({ type: 'image', url, ...(altTitle ? { alt: altTitle } : {}) })
        break
      }
      case 'video':
        out.push({ type: 'video', url })
        break
      case 'audio':
        out.push({ type: 'audio', url })
        break
      case 'file': {
        const filename =
          typeof raw.payload?.title === 'string' ? raw.payload.title : undefined
        out.push({ type: 'file', url, ...(filename ? { filename } : {}) })
        break
      }
      default:
        // Unknown attachment types fall back to `file` so the chat UI at
        // least gets a downloadable link. This keeps the adapter permissive
        // when FB ships a new attachment shape we haven't seen yet.
        out.push({ type: 'file', url })
        break
    }
  }
  return out
}

// ============================================================================
// Time helpers
// ============================================================================

function isoFromTimestamp(msOrZero: number): string {
  if (!Number.isFinite(msOrZero) || msOrZero <= 0) {
    return new Date(0).toISOString()
  }
  return new Date(msOrZero).toISOString()
}

// ============================================================================
// Adapter export
// ============================================================================

export const facebookMessengerWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}
